import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getInvoice } from "../services/InvoiceService";
import type { Invoice } from "../types/invoice.types";
import InvoiceOverviewContext from "./InvoiceOverviewContext";
import Section from "./Section";

const InvoiceOverview = () => {
  document.title = getDocumentTitle("Invoice overview");
  const { id } = useParams();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!id) return;
    const numericId = Number(id);
    try {
      const res = await getInvoice(numericId);
      if (res.success) {
        setInvoice(res.data);
      } else {
        setInvoice(null);
      }
    } catch (err) {
      console.warn(err);
      setInvoice(null);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      await refetch();
      if (!cancelled) setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, refetch]);

  if (loading) return <PageLoadingSpinner message="Loading invoice..." />;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Invoice Details"
          pageTitle="Finance"
          items={[{ label: "Invoice", path: "/finance/invoices" }]}
        />
        <InvoiceOverviewContext.Provider value={{ invoice, refetch }}>
          <Section />
        </InvoiceOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default InvoiceOverview;
